/**
 * AuthContext — Supabase auth surface for the EffectiveSLP web app.
 *
 * Wire up:
 *  - current `user` + `session` (re-renders on auth-state changes)
 *  - signInWithEmail — magic-link via signInWithOtp (the primary path)
 *  - signUpWithEmail — same surface as signIn (Supabase creates
 *    the user on first OTP)
 *  - signInWithGoogle — Supabase OAuth with Google (secondary path)
 *  - signOut
 *
 * Apple Sign-In is intentionally not exposed on the web tier.
 * Apple Guideline 4.8 requires Sign in with Apple on iOS apps that
 * offer another social login; web apps are not covered. The iOS
 * app retains its Apple Sign-In button.
 *
 * The redirect URL after magic-link confirmation is
 * `${window.location.origin}/auth/callback` per getAuthRedirectTo().
 */
import { createContext, useEffect, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../supabase/client';

export interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithEmail: (input: { email: string }) => Promise<void>;
  signUpWithEmail: (input: { email: string }) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
export { AuthContext };

function getAuthRedirectTo(): string {
  // On web, the magic-link confirmation lands the user back on
  // this app's `/auth/callback` route. Cloudflare Pages serves
  // index.html for that path (see web/public/_redirects) and the
  // SPA's router then calls supabase.auth.setSession with the
  // URL-fragment tokens.
  //
  // VITE_AUTH_REDIRECT_TO is set per environment:
  //   - dev (localhost): http://localhost:5174/auth/callback
  //   - staging (effectiveslp.pages.dev): https://effectiveslp.pages.dev/auth/callback
  //   - prod (app.effectiveslp.com): https://app.effectiveslp.com/auth/callback
  if (typeof window !== 'undefined') {
    return (
      import.meta.env.VITE_AUTH_REDIRECT_TO ??
      `${window.location.origin}/auth/callback`
    );
  }
  // SSR / prerender fallback (we don't currently SSR but Vite reads this
  // at config time).
  return import.meta.env.VITE_AUTH_REDIRECT_TO ?? '/auth/callback';
}

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): ReactElement {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      setLoading(false);
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);
    });
    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  async function signInWithEmail(input: { email: string }): Promise<void> {
    const { error } = await supabase.auth.signInWithOtp({
      email: input.email,
      options: { emailRedirectTo: getAuthRedirectTo() },
    });
    if (error) throw new Error(error.message);
  }

  async function signUpWithEmail(input: { email: string }): Promise<void> {
    // Same surface as sign-in; Supabase creates the user on first OTP.
    return signInWithEmail(input);
  }

  async function signInWithGoogle(): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: getAuthRedirectTo(),
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    if (error) throw new Error(error.message);
  }

  async function signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message);
  }

  const value: AuthContextValue = {
    user: session?.user ?? null,
    session,
    loading,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
